import { BLEND_MODES, Container, Graphics, Sprite, Texture } from "pixi.js"
import { ScalingParams } from "../../../../client_util"

/**
 * The "there is loot here" cue on a dropped item.
 *
 * Two things make this read as light rather than as a sticker stuck over the loot:
 *
 * 1. THE ITEM'S OWN ART is what lights up. Two enlarged additive copies of its texture are drawn
 *    under an opaque copy of the item, so the item covers their middles and what survives is a
 *    glowing fringe in exactly its silhouette; a third copy over the top lifts its own pixels a
 *    little. You can read what you are about to pick up right through the brightest frame of the
 *    flash, which a blob of white over the top cannot do.
 * 2. It is drawn in the WORLD OVERLAY, above everything in the world. A drop that rolled under a
 *    tree canopy or behind a wall still shows its lit shape through them for the length of the
 *    flash, which is the whole point of a discovery cue -- the loot you need told about is exactly
 *    the loot you cannot see. The item's own renderable keeps drawing in the world underneath at
 *    its proper depth; this copy fades up and down with the flash and is gone between them.
 *
 * Everything that emits is on an ADD layer -- a normal-blend highlight over grass comes out as dust
 * rather than light (see the fire notes beside this file).
 *
 * Quiet is most of the cycle on purpose: a camp floor can hold a dozen drops after a wave, and
 * twelve things pulsing at once is a christmas tree. Each item flashes for
 * DROPPED_ITEM_GLINT_WINDOW_MS out of DROPPED_ITEM_GLINT_PERIOD_MS, phase-staggered by its id, and
 * between flashes only a trace of light on the ground is left.
 */
export const DROPPED_ITEM_GLINT_PERIOD_MS = 3400
export const DROPPED_ITEM_GLINT_WINDOW_MS = 850

/** Fast attack, long tail: a symmetrical sine looks like a lamp being turned up and down. */
const RISE = 0.16
const FALL = 1.7

// Warm, and starved of green: additive orange over grass lands on lime if the green is left in
// (the Ember Core blast learned this the hard way), so the outer glow is pushed towards red and
// only the tight fringe and the sparkle are allowed near white.
const INK = {
    glow: 0xff8a24,
    spark: 0xffb347,
    hot: 0xfff0dc,
    pool: 0xffa83c,
    white: 0xffffff,
}

const GLOW_PX = 6 // how far the soft outer glow reaches past the silhouette, in world pixels
const GLOW_ALPHA = 0.13
const HALO_PX = 2.5 // the tight hot fringe that actually draws the shape
const HALO_ALPHA = 0.55
const SHINE_ALPHA = 0.2 // the item's own pixels, lifted -- deliberately small
const POOL_ALPHA = 0.1
const POOL_IDLE = 0.12 // what is left on the ground between flashes
const SPARK_ARM = 7
const SPARK_ASPECT = 0.7 // short axis over long: nearly square, or it reads as a scratch
const SPARK_WAIST = 0.26
const SPARK_TRAVEL = 0.8 // how far along the item's long axis the sparkle slides, as a fraction

const smoothstep = (t: number) => (t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t))

/** The flash's brightness at `elapsed` ms into a cycle: 0 outside the window, 1 at its peak. */
export const glintEnvelope = (elapsed: number) => {
    const u = elapsed / DROPPED_ITEM_GLINT_WINDOW_MS
    if (u < 0 || u > 1) {
        return 0
    }
    return u < RISE ? smoothstep(u / RISE) : Math.pow(1 - (u - RISE) / (1 - RISE), FALL)
}

/**
 * What the glint needs to know about the item it belongs to, read fresh every frame.
 *
 * A reference to the renderable rather than a copy of the entity's location: a collectable with an
 * expiry SPINS (BasicSprite.startRotation), and the renderable also carries the lift its own art
 * height earns it (BaseRenderable.getYOffset), so the only transform that is always right is the
 * one the item is actually being drawn with.
 */
export interface GlintSubject {
    /** Where the item's art is being drawn, in world pixels. */
    readonly x: number
    readonly y: number
    /** False while the item is culled or hidden; the glint goes with it. */
    readonly visible: boolean
    /** Null once the renderable has left the world surface. */
    readonly parent: unknown
    /** The item's sprite and the angle it is being drawn at, or undefined if it has no art yet. */
    glintArt(): { sprite: Sprite; rotation: number } | undefined
}

/** A four-point star: `arm` along `rot`, `arm * aspect` across it, pinched to `arm * waist`. */
const star = (
    g: Graphics,
    cx: number,
    cy: number,
    arm: number,
    rot: number,
    color: number,
    alpha: number,
    aspect: number,
    waist: number,
) => {
    const points: number[] = []
    for (let i = 0; i < 4; i++) {
        const tip = rot + (i * Math.PI) / 2
        const len = i % 2 === 0 ? arm : arm * aspect
        points.push(cx + Math.cos(tip) * len, cy + Math.sin(tip) * len)
        const between = tip + Math.PI / 4
        points.push(cx + Math.cos(between) * arm * waist, cy + Math.sin(between) * arm * waist)
    }
    g.beginFill(color, alpha)
    g.drawPolygon(points)
    g.endFill()
}

export class DroppedItemGlint extends Container {
    // Light on the ground and the sparkle in the air stay level while a spinning item turns.
    private readonly pool: Graphics
    private readonly spark: Graphics
    // Everything shaped like the item turns with it.
    private readonly turned: Container
    private readonly glow: Sprite
    private readonly halo: Sprite
    private readonly body: Sprite
    private readonly shine: Sprite

    private readonly subject: GlintSubject
    private readonly phaseOffset: number
    private readonly tilt: number
    private readonly drift: number

    private tracing?: Texture
    private texWidth = 1
    private texHeight = 1
    private baseScaleX = 1
    private baseScaleY = 1
    private poolRadius = 10
    private poolY = 6
    // Where the art's middle sits inside the item's container. Zero for an atlas item
    // (the frame carries its own centred anchor), but a `tileset:id` sprite is placed by
    // hand -- half its width left and a flat 14px up -- and the ground pool and the
    // sparkle belong on the ART, not on the container's origin.
    private artX = 0
    private artY = 0
    private lit = -1

    constructor(entityId: string, subject: GlintSubject) {
        super()
        this.subject = subject
        // A stable per-item phase: a pile of loot that all flashed on the same frame would read as
        // one blinking object rather than as several things worth picking up.
        let hash = 0
        for (let i = 0; i < entityId.length; i++) {
            hash = (Math.imul(hash, 31) + entityId.charCodeAt(i)) >>> 0
        }
        // ...which needs the hash AVALANCHED, not just accumulated. A plain polynomial hash turns a
        // one-character difference into a one-unit difference, and the ids a single drop event mints
        // share every character but their last few -- six items dropped together came out with
        // phases a millisecond apart and flashed as one thing. Seen in the running game.
        hash ^= hash >>> 16
        hash = Math.imul(hash, 0x45d9f3b) >>> 0
        hash ^= hash >>> 16
        this.phaseOffset = hash % DROPPED_ITEM_GLINT_PERIOD_MS
        this.tilt = ((hash % 97) / 97 - 0.5) * 0.5
        this.drift = ((hash % 61) / 61) * Math.PI * 2

        this.interactive = false
        this.interactiveChildren = false
        // Over the world, under the overlay's own nameplates and prompts.
        this.zIndex = -1
        this.visible = false

        this.pool = new Graphics()
        this.pool.blendMode = BLEND_MODES.ADD
        this.addChild(this.pool)

        this.turned = new Container()
        this.addChild(this.turned)
        this.glow = this.silhouette(BLEND_MODES.ADD)
        this.halo = this.silhouette(BLEND_MODES.ADD)
        // The item itself, over its own fringe: this is the copy that shows through a tree.
        this.body = this.silhouette(BLEND_MODES.NORMAL)
        this.shine = this.silhouette(BLEND_MODES.ADD)

        this.spark = new Graphics()
        this.spark.blendMode = BLEND_MODES.ADD
        this.addChild(this.spark)
    }

    private silhouette = (blendMode: BLEND_MODES) => {
        const sprite = new Sprite(Texture.EMPTY)
        sprite.blendMode = blendMode
        this.turned.addChild(sprite)
        return sprite
    }

    /** True while the item this belongs to is still on the world surface. */
    alive = () => !this.destroyed && !!this.subject.parent

    /**
     * Point the copies at the item's current art. Re-run whenever the texture changes, which it
     * does when the sprite behind an item is rebuilt -- otherwise the glow traces a shape the item
     * no longer wears.
     */
    private trace = (model: Sprite) => {
        const texture = model.texture
        this.tracing = texture
        this.texWidth = Math.max(1, texture.width)
        this.texHeight = Math.max(1, texture.height)
        this.baseScaleX = model.scale.x
        this.baseScaleY = model.scale.y
        // Art this size wants a pool roughly as wide as it is, sitting just under its lowest edge.
        this.poolRadius = 6 + Math.max(this.texWidth, this.texHeight) * 0.25
        this.poolY = 2 + Math.min(this.texWidth, this.texHeight) * 0.35
        this.artX = model.position.x + (0.5 - model.anchor.x) * this.texWidth * this.baseScaleX
        this.artY = model.position.y + (0.5 - model.anchor.y) * this.texHeight * this.baseScaleY
        for (const sprite of [this.glow, this.halo, this.body, this.shine]) {
            sprite.texture = texture
            sprite.anchor.copyFrom(model.anchor)
            sprite.position.copyFrom(model.position)
        }
        this.lit = -1
    }

    /** Grow a copy `px` world pixels past the item's edge on every side. */
    private grow = (sprite: Sprite, px: number) => {
        sprite.scale.set(
            this.baseScaleX * (1 + (2 * px) / this.texWidth),
            this.baseScaleY * (1 + (2 * px) / this.texHeight),
        )
    }

    animate = (now: number) => {
        const art = this.subject.glintArt()
        this.visible = this.subject.visible && !!this.subject.parent && !!art
        if (!this.visible) {
            return
        }
        if (art.sprite.texture !== this.tracing) {
            this.trace(art.sprite)
        }

        const zoom = ScalingParams.surfaceZoom
        this.position.set(this.subject.x * zoom, this.subject.y * zoom)
        this.scale.set(zoom)
        this.turned.rotation = art.rotation

        const elapsed = (now + this.phaseOffset) % DROPPED_ITEM_GLINT_PERIOD_MS
        const a = glintEnvelope(elapsed)
        // Most frames of most items land here: between flashes nothing about this drawing changes,
        // so a camp floor full of loot costs a modulo each rather than a geometry rebuild each.
        if (a === 0 && this.lit === 0) {
            return
        }
        this.lit = a

        // The art's middle, carried round by whatever angle the item is lying at.
        const cos = Math.cos(art.rotation)
        const sin = Math.sin(art.rotation)
        const cx = cos * this.artX - sin * this.artY
        const cy = sin * this.artX + cos * this.artY

        this.pool.clear()
        const lit = POOL_ALPHA * (POOL_IDLE + (1 - POOL_IDLE) * a)
        const radius = this.poolRadius * (1 + 0.25 * a)
        this.pool.beginFill(INK.pool, lit)
        this.pool.drawEllipse(cx, cy + this.poolY, radius, radius * 0.42)
        this.pool.endFill()
        this.pool.beginFill(INK.hot, lit * 0.9)
        this.pool.drawEllipse(cx, cy + this.poolY, radius * 0.5, radius * 0.22)
        this.pool.endFill()

        // The item's shape only exists over the world while the light is on it; the rest of the
        // time the drop is whatever the world surface makes of it, at its own depth.
        this.turned.visible = a > 0
        if (a > 0) {
            this.glow.alpha = GLOW_ALPHA * a
            this.glow.tint = INK.glow
            this.grow(this.glow, GLOW_PX * (0.5 + 0.5 * a))
            this.halo.alpha = HALO_ALPHA * a
            this.halo.tint = INK.hot
            this.grow(this.halo, HALO_PX * (0.4 + 0.6 * a))
            this.body.alpha = a
            this.body.tint = art.sprite.tint
            this.body.scale.set(this.baseScaleX, this.baseScaleY)
            this.shine.alpha = SHINE_ALPHA * a
            this.shine.tint = INK.hot
            this.shine.scale.set(this.baseScaleX, this.baseScaleY)
        }

        this.spark.clear()
        if (a <= 0.02) {
            return
        }
        // The sparkle slides along the item's long axis over the first half of the window: a
        // highlight travelling across a thing is what says "this caught the light", where a
        // highlight sitting still says "something is drawn on top of this".
        const u = elapsed / DROPPED_ITEM_GLINT_WINDOW_MS
        const reach = smoothstep(Math.min(1, u / 0.55)) * 2 - 1
        const distance = reach * (this.texWidth / 2) * this.baseScaleX * SPARK_TRAVEL
        const sx = cx + cos * distance
        const sy = cy + sin * distance
        const k = Math.sin(Math.min(1, u / 0.7) * Math.PI)
        const arm = SPARK_ARM * (0.3 + 0.7 * k)
        this.spark.beginFill(INK.spark, 0.3 * k)
        this.spark.drawCircle(sx, sy, arm * 0.42)
        this.spark.endFill()
        star(this.spark, sx, sy, arm, this.tilt, INK.hot, 0.8 * k, SPARK_ASPECT, SPARK_WAIST)
        star(this.spark, sx, sy, arm * 0.45, this.tilt + Math.PI / 4, INK.spark, 0.45 * k, 0.55, 0.1)
        this.spark.beginFill(INK.white, 0.9 * k)
        this.spark.drawCircle(sx, sy, 0.8 + 0.7 * k)
        this.spark.endFill()
        // Three specks lifting off the item: what turns a flash into an ember.
        for (let i = 0; i < 3; i++) {
            const m = (u - 0.08 * i) / 0.75
            if (m <= 0 || m >= 1) {
                continue
            }
            this.spark.beginFill(INK.hot, 0.8 * (1 - m) * (1 - m))
            this.spark.drawCircle(cx + Math.sin(this.drift + i * 2.1) * 5, cy + 2 - m * 13 - i * 1.5, 0.9 - 0.4 * m)
            this.spark.endFill()
        }
    }
}
